// The Coverage_Floor input and its percent/share conversions (blueprint-sets Req 2.8).
import Box from '@cloudscape-design/components/box';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { coverageFloorSchema } from '@bda/shared';

// ---------- Coverage floor (Req 2.8) ----------

export const COVERAGE_FLOOR_LABEL = 'Coverage floor';
export const COVERAGE_FLOOR_HINT =
  "Segments with fewer fields found than this share are marked 'Possible wrong blueprint'. 0 turns the check off.";
/** Same text as the API's 400 at `fieldErrors.coverageFloor`. */
export const COVERAGE_FLOOR_RANGE_MESSAGE = 'The coverage floor must be between 0 and 100 %.';
export const COVERAGE_FLOOR_NUMBER_MESSAGE = 'Enter a number from 0 to 100.';

/** A stored share (0..1) as the percent the input shows (0.3 -> 30), without float noise. */
export const shareToPercent = (share: number): number => Math.round(share * 10_000) / 100;
/** A percent (0..100) as the stored share (30 -> 0.3), without float noise. */
export const percentToShare = (percent: number): number => Math.round(percent * 100) / 10_000;
/** The input text of a stored share. */
export const coverageFloorText = (share: number): string => String(shareToPercent(share));

/** Parses the "Coverage floor" input (percent) into the share to store, validated with `coverageFloorSchema`. */
export function parseCoverageFloor(text: string): { share: number } | { error: string } {
  const t = text.trim();
  const n = t === '' ? Number.NaN : Number(t);
  if (!Number.isFinite(n)) return { error: COVERAGE_FLOOR_NUMBER_MESSAGE };
  if (n < 0 || n > 100) return { error: COVERAGE_FLOOR_RANGE_MESSAGE };
  const share = percentToShare(n);
  return coverageFloorSchema.safeParse(share).success ? { share } : { error: COVERAGE_FLOOR_RANGE_MESSAGE };
}

export interface CoverageFloorInputProps {
  /** The input text, in percent. */
  value: string;
  onChange?: (text: string) => void;
  /** The browser's or the API's message (`fieldErrors.coverageFloor`). */
  errorText?: string | null;
  /** A Set_Version view. */
  readOnly?: boolean;
  controlId?: string;
}

/** "Coverage floor" number input in percent (0-100, step 1) with the design's hint. */
export function CoverageFloorInput({
  value,
  onChange,
  errorText,
  readOnly = false,
  controlId = 'coverage-floor',
}: CoverageFloorInputProps) {
  return (
    <FormField
      label={COVERAGE_FLOOR_LABEL}
      description={COVERAGE_FLOOR_HINT}
      errorText={errorText ?? undefined}
      controlId={controlId}
    >
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Input
          controlId={controlId}
          type="number"
          inputMode="decimal"
          step={1}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange?.(e.detail.value)}
        />
        <Box>%</Box>
      </SpaceBetween>
    </FormField>
  );
}
